import { useMemo } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import { useHomepagePageData } from '#src/app/pages/homepage/pageContext';
import { getMarqueeCardImageUrl } from '#src/app/pages/homepage/shared/utils';

export type PetitionMarqueeCardData = {
	key: string;
	title: string;
	imageUrl: string;
	signaturesLabel: string;
	href: string;
	isVictory: boolean;
};

type Result = ModelHookResult<
	{
		petitions: PetitionMarqueeCardData[];
	},
	{
		trackPetitionClick: (petitionId: string) => void;
	}
>;

export function usePetitionMarquee(): Result {
	const {
		data: { petitionMarqueeItems },
	} = useHomepagePageData();
	const { translatePlural } = useI18n();
	const track = useTracking();

	// memoized so useMarqueeRows only re-samples when the page data actually changes
	const petitions = useMemo(
		() =>
			petitionMarqueeItems.flatMap(({ summarizedTitle, usePetitionPhoto, petition }) => {
				const { displayed, displayedLocalized } = petition.signatureState.signatureCount;

				// an entry with no usable image is dropped rather than shown with a placeholder
				const imageUrl = getMarqueeCardImageUrl({ usePetitionPhoto, petition });
				if (!imageUrl) return [];

				return [
					{
						key: petition.id,
						title: summarizedTitle,
						imageUrl,
						signaturesLabel: translatePlural('corgi.components.petition_card.signatures', displayed, {
							countLocalized: displayedLocalized,
						}),
						href: `/p/${encodeURIComponent(petition.slug)}?source_location=homepage_petition_marquee`,
						isVictory: petition.status === 'VICTORY',
					},
				];
			}),
		[petitionMarqueeItems, translatePlural],
	);

	return {
		data: {
			petitions,
		},
		actions: {
			trackPetitionClick: (petitionId: string) => {
				void track('homepage_petition_marquee_click', { petition_id: petitionId });
			},
		},
	};
}
